import { useEffect, useState, useMemo } from "react";
import { getEquipmentBrand, getEmsBootEquipments } from "../../../../../api";
import useUserStore from "../../../../stores/user";

// NOTE: This version may change as we make BrandItem more composable
// and make it work for create app page.
export function useEquipmentsByTypeAndBrand() {
  const userLoading = useUserStore((state) => state.userLoading);
  const organizationId = useUserStore((state) => state.organizationId);
  const [equipments, setEquipments] = useState([]);
  const [categorizedEquipments, setCategorizedEquipments] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userLoading) {
      return;
    }

    async function fetchData() {
      try {
        setLoading(true);
        const [brandRes, equipmentRes] = await Promise.all([
          getEquipmentBrand({ all: "all" }),
          getEmsBootEquipments({ org_id: organizationId }),
        ]);

        if (!brandRes.status || !equipmentRes.status) {
          throw new Error("Failed to fetch equipments or brands");
        }

        setBrands(brandRes.data);
        setCategorizedEquipments(equipmentRes.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [userLoading, organizationId]);

  const groupedEquipments = useMemo(() => {
    if (!categorizedEquipments.length) return [];
    const brandMap = new Map(brands.map((b) => [b.id, b]));
    const allEquipments = [];
    const grouped = {
      gateway: {
        categoryIds: [5],
        name: "Gateways",
        brands: new Map(),
        unbranded: [],
      },
      mobileSystemsAndTerminals: {
        categoryIds: [1, 2, 3],
        name: "Mobile Systems & Terminals",
        brands: new Map(),
        unbranded: [],
      },
      posSystems: {
        categoryIds: [4],
        name: "Point-of-Sale Systems",
        brands: new Map(),
        unbranded: [],
      },
      addOns: {
        categoryIds: [6],
        name: "Add-ons",
        brands: new Map(),
        unbranded: [],
      },
    };

    // Iterate through all categories and their equipments
    categorizedEquipments.forEach((category) => {
      // Find which group this category belongs to
      let targetGroup = null;
      for (const [groupKey, group] of Object.entries(grouped)) {
        if (group.categoryIds.includes(category.category)) {
          targetGroup = group;
          break;
        }
      }

      if (!targetGroup) return; // Skip if no matching group found

      (category.add_ons || []).forEach((eq) => allEquipments.push(eq));

      // Process all equipments in this category
      category.equipments.forEach((eq) => {
        allEquipments.push(eq);

        if (!eq.brand_id) {
          targetGroup.unbranded.push(eq);
        } else {
          const brand = brandMap.get(eq.brand_id);
          if (!brand) return; // Skip if brand not found

          if (!targetGroup.brands.has(eq.brand_id)) {
            targetGroup.brands.set(eq.brand_id, {
              id: brand.id,
              name: brand.name,
              image: brand.image,
              description: brand.description,
              equipments: [],
            });
          }
          targetGroup.brands.get(eq.brand_id).equipments.push(eq);
        }
      });

      setEquipments(allEquipments);
    });

    return Object.values(grouped).map((group) => ({
      ...group,
      brands: Array.from(group.brands.values()),
    }));
  }, [categorizedEquipments, brands]);

  return {
    equipments,
    groupedEquipments,
    loading: userLoading || loading,
    error,
  };
}
